import { useCallback, useEffect, useRef } from "react"
import { TOAST_WITH_ACTION_DURATION } from "@/constants/ui"
import { Timestamp } from "@/types/date"
import { useIdleTimer } from "react-idle-timer"

import { APP_INSTANCE_ID } from "@/lib/app-instance"
import { AppVersionEvents } from "@/lib/app-version-events"
import { refreshTokenIfNeeded } from "@/lib/auth/client"
import { addMinutes, afterTimestamp, now } from "@/lib/date-utils"
import { isEmbedded } from "@/lib/is-embedded"
import { mtLocation } from "@/lib/mt-location"
import { useRouter } from "@/lib/router"
import { useAppStore } from "@/lib/storage/hooks"
import { selectProfileSetupDialogOpen } from "@/lib/storage/user-slice"
import { toastManager } from "@/lib/toast"
import { RELEASE_VERSION } from "@/lib/version"
import { BotAvatar } from "@/components/ui/avatar"

const MINUTE_MS = 60_000

// Fresh dynos need several minutes to load modules and settle their heaps. Once
// they are warm, spread browser refreshes out so a deploy does not make every
// connected client request the new build's assets at once.
export const VERSION_UPDATE_MIN_DELAY_MS = 8 * MINUTE_MS
export const VERSION_UPDATE_SPREAD_MS = 15 * MINUTE_MS

function stableHash(value: string): number {
  let hash = 0x811c9dc5
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

export function versionUpdateDelayMs(
  version: string,
  appInstanceId = APP_INSTANCE_ID
): number {
  return (
    VERSION_UPDATE_MIN_DELAY_MS +
    (stableHash(`${appInstanceId}:${version}`) % VERSION_UPDATE_SPREAD_MS)
  )
}

/**
 * ActivityMonitor component monitors user activity and handles:
 * - Version updates: displays toast when new version is available, auto-reloads
 *   when user becomes active after being idle or refocuses the browser tab
 * - Token refresh: proactively refreshes auth tokens when user becomes active
 *
 * this is way high up in the component tree, be careful what you import/use here
 */
export function ActivityMonitor() {
  const lastNotifiedRef = useRef<Timestamp | null>(null)
  const updatedVersionRef = useRef<string | null>(null)
  const pendingVersionRef = useRef<string | null>(null)
  const versionActivationTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  const updatePending = useRef(false)
  const idleRef = useRef(false)
  const store = useAppStore()
  const router = useRouter()

  const performReload = useCallback(async () => {
    await router.replace(
      {
        query: {
          ...router.query,
          "version-update": updatedVersionRef.current ?? "",
        },
      },
      undefined,
      { shallow: true }
    )
    mtLocation.reload()
  }, [router])

  const requestReload = useCallback(async () => {
    if (selectProfileSetupDialogOpen(store.getState())) {
      return
    }

    await performReload()
  }, [performReload, store])

  const showUpdateToast = useCallback(() => {
    lastNotifiedRef.current = now()
    toastManager.add({
      description: "A new version of Mindtrip is available.",
      timeout: TOAST_WITH_ACTION_DURATION,
      data: {
        leading: <BotAvatar size="xs" className="mr-1 shrink-0" />,
      },
      actionProps: {
        children: "Update",
        onClick: () => requestReload(),
      },
      onClose: () => {
        // Schedule re-notification in 10 minutes
        const updatedVersion = updatedVersionRef.current
        if (updatedVersion) {
          setTimeout(
            () => {
              AppVersionEvents.instance.emit(updatedVersion)
            },
            1000 * 60 * 10
          )
        }
      },
    })
  }, [requestReload])

  useIdleTimer({
    onIdle: () => {
      idleRef.current = true
    },
    onActive: () => {
      const shouldReload =
        idleRef.current &&
        updatePending.current &&
        !selectProfileSetupDialogOpen(store.getState())

      idleRef.current = false

      // Auto-reload when user becomes active after being idle
      if (shouldReload) {
        void requestReload()
        return
      }

      void refreshTokenIfNeeded()
    },
    timeout: 5 * MINUTE_MS,
    throttle: 500,
  })

  const activatePendingVersion = useCallback(
    (version: string) => {
      if (pendingVersionRef.current !== version) {
        return
      }

      pendingVersionRef.current = null
      versionActivationTimerRef.current = null
      updatedVersionRef.current = version
      updatePending.current = true

      if (selectProfileSetupDialogOpen(store.getState())) {
        return
      }

      showUpdateToast()
    },
    [showUpdateToast, store]
  )
  const activatePendingVersionRef = useRef(activatePendingVersion)

  useEffect(() => {
    activatePendingVersionRef.current = activatePendingVersion
  }, [activatePendingVersion])

  useEffect(
    () => () => {
      if (versionActivationTimerRef.current != null) {
        clearTimeout(versionActivationTimerRef.current)
      }
    },
    []
  )

  // Auto-reload when user refocuses the tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && updatePending.current) {
        void requestReload()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [requestReload])

  useEffect(() => {
    if (router.query["version-update"]) {
      // remove the version-update query param if it exists
      const updatedQuery = { ...router.query }
      delete updatedQuery["version-update"]
      void router.replace(
        {
          query: updatedQuery,
        },
        undefined,
        { shallow: true }
      )
    }

    const listener = (version: string) => {
      // don't notify if the version is the same as the current version
      if (!version || RELEASE_VERSION === version) {
        return
      }

      // Embedded iframe gets fresh code on next parent page load;
      // hard-reloading the iframe re-triggers initEmbeddedApp which
      // re-processes hash params (prompt, etc.) and creates new chats
      if (isEmbedded()) {
        return
      }

      // Re-notification for a version that is already active keeps the existing
      // 10-minute cadence and should not enter the rollout delay again.
      if (updatedVersionRef.current === version && updatePending.current) {
        if (
          lastNotifiedRef.current != null &&
          !afterTimestamp(addMinutes(now(), -10), lastNotifiedRef.current)
        ) {
          return
        }

        if (!selectProfileSetupDialogOpen(store.getState())) {
          showUpdateToast()
        }
        return
      }

      // Duplicate socket events must not move a client into a later cohort.
      if (pendingVersionRef.current === version) {
        return
      }

      pendingVersionRef.current = version
      if (versionActivationTimerRef.current != null) {
        clearTimeout(versionActivationTimerRef.current)
      }

      const delayMs = versionUpdateDelayMs(version)

      console.debug(
        "version change detected: new, current, update delay ms",
        version,
        RELEASE_VERSION,
        delayMs
      )

      versionActivationTimerRef.current = setTimeout(
        () => activatePendingVersionRef.current(version),
        delayMs
      )
    }

    AppVersionEvents.instance.addListener(listener)

    return () => {
      AppVersionEvents.instance.removeListener(listener)
    }
  }, [router, showUpdateToast, store])

  return null
}
